import { create } from 'zustand'

/**
 * User interface for authentication
 */
export interface User {
  id: number | string
  email: string
  first_name: string
  last_name: string
  avatar_url?: string | null
  email_verified?: boolean
  is_staff: boolean
  is_superuser: boolean
  is_kmp_admin: boolean
}

/**
 * Auth Store - In-Memory ONLY (Security Priority)
 * 
 * CRITICAL: This store uses NO persist middleware.
 * Access tokens are stored in memory only to prevent XSS attacks.
 * User will be logged out on page refresh.
 */
interface AuthStore {
  accessToken: string | null
  user: User | null
  isAuthenticated: boolean
  setAccessToken: (token: string) => void
  setUser: (user: User) => void
  logout: () => void
}

export const useAuthStore = create<AuthStore>((set) => ({
  accessToken: null,
  user: null,
  isAuthenticated: false,
  setAccessToken: (token: string) => {
    set({ accessToken: token, isAuthenticated: true })
  },
  setUser: (user: User) => {
    set({ user })
  },
  logout: () => {
    set({ accessToken: null, user: null, isAuthenticated: false })
  },
}))

/**
 * Organization Store
 * 
 * Holds the active organization ID from URL (/o/:orgId).
 * This is NOT persisted - it will be synced from the router.
 */
interface OrgStore {
  activeOrgId: string | null
  activeOrgRole: 'OWNER' | 'ADMIN' | 'VIEWER' | null
  setActiveOrgId: (id: string | null) => void
  setActiveOrgRole: (role: OrgStore['activeOrgRole']) => void
}

export const useOrgStore = create<OrgStore>((set) => ({
  activeOrgId: null,
  activeOrgRole: null,
  setActiveOrgId: (id: string | null) => {
    set({ activeOrgId: id })
  },
  setActiveOrgRole: (role) => {
    set({ activeOrgRole: role })
  },
}))

/**
 * Header contextual usage limits.
 *
 * Page-level features can expose compact usage counters in the persistent
 * dashboard header without coupling the layout to a specific page query.
 */
export interface HeaderUsageLimits {
  trackedQueriesUsed: number
  trackedQueriesLimit: number
  trackedProductsUsed: number
  trackedProductsLimit: number
}

export interface HeaderCompanion {
  visible: boolean
  busy: boolean
  message: string
}

interface HeaderUsageLimitsStore {
  limits: HeaderUsageLimits | null
  companion: HeaderCompanion | null
  setLimits: (limits: HeaderUsageLimits | null) => void
  setCompanion: (companion: HeaderCompanion | null) => void
}

export const useHeaderUsageLimitsStore = create<HeaderUsageLimitsStore>((set) => ({
  limits: null,
  companion: null,
  setLimits: (limits) => set({ limits }),
  setCompanion: (companion) => set({ companion }),
}))
